import { ArrowForwardIcon, ArrowRightIcon, EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import React, { MutableRefObject, useEffect, useState } from 'react';
import { BiCaretRight } from 'react-icons/bi';
import InputMask from 'react-input-mask';

import { CaretDownIcon } from '../../../../../assets/icons/CarretDownIcon';
import { CarretRightIcon } from '../../../../../assets/icons/CarretRightIcon';
import { PencilIcon } from '../../../../../assets/icons/PencilIcon';
import {
  requestConsentByCpf,
  RequestConsentByCPFProps
} from '../../../../../services/previdencia-individual/carteira_previdencia.service';
import { formatNumber, phone, validEmail } from '../../../../../utils/masks/';

type SingleLgpdModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: { cpf: string; email?: string; celular?: string };
  onSuccessRequest?: ({ cpf }: { cpf?: string }) => void;
};
export const SingleLgpdModal = ({
  isOpen,
  onClose,
  data,
  onSuccessRequest
}: SingleLgpdModalProps) => {
  const toast = useToast();

  const [emailError, setEmailError] = useState<string>();
  const [smsError, setSmsError] = useState<string>();
  const emailRef = React.useRef<HTMLInputElement>(null);
  const smsRef = React.useRef<any>(null);

  //Mutations
  const emailMutation = useMutation({
    mutationFn: (data: RequestConsentByCPFProps) => requestConsentByCpf(data),
    onSettled: (data, error) => {
      if (error) {
        console.log({ error });
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro ao solicitar o consentimento.',
          description: 'Tente novamente mais tarde!',
          isClosable: true
        });
      }
    },
    onSuccess: (_) => {
      handleClose();

      toast({
        status: 'success',
        title: 'Solicitação efetuada com sucesso!',
        //description: 'Tente novamente mais tarde!',
        isClosable: true
      });
      if (onSuccessRequest) onSuccessRequest({ cpf: data?.cpf });
    }
  });
  const smsMutation = useMutation({
    mutationFn: (data: RequestConsentByCPFProps) => requestConsentByCpf(data),
    onSettled: (data, error) => {
      if (error) {
        console.log({ error });
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro ao solicitar o consentimento.',
          description: 'Tente novamente mais tarde!',
          isClosable: true
        });
      }
    },
    onSuccess: (_) => {
      handleClose();

      toast({
        status: 'success',
        title: 'Solicitação efetuada com sucesso!',
        //description: 'Tente novamente mais tarde!',
        isClosable: true
      });
      if (onSuccessRequest) onSuccessRequest({ cpf: data?.cpf });
    }
  });

  const handleClose = () => {
    setEmailError(undefined);
    onClose();
  };

  const handleEmailSubmit = () => {
    const emailRefCurrent = emailRef.current;
    if (!data?.email && !emailRefCurrent?.value) {
      setEmailError(
        'Não foi encontrado nenhum email em nossa base de dados. Por favor, forneça um para continuar.'
      );

      return;
    }
    if (
      !emailRefCurrent?.disabled &&
      !emailRefCurrent?.value &&
      !validEmail(emailRefCurrent?.value || '')
    ) {
      setEmailError('Por favor, digite um endereço de email valido');
      return;
    }

    const emailSend = emailRefCurrent?.value ?? null;

    if (data) {
      emailMutation.mutate({ type: 'email', email: emailSend, cpf: data.cpf });
    }
    setEmailError(undefined);
  };

  const handleSMSSubmit = () => {
    const smsRefCurrent = smsRef.current;

    if (!data?.celular && !smsRefCurrent?.value) {
      setSmsError(
        'Não foi encontrado nenhum celular em nossa base de dados. Por favor, forneça um para continuar.'
      );

      return;
    }
    console.log(smsRefCurrent);
    if (!smsRefCurrent?.props.disabled && !smsRefCurrent?.value) {
      setSmsError('Por favor, digite um número de celular valido');
      return;
    }

    const smsSend = smsRefCurrent?.value ?? null;

    if (data) {
      smsMutation.mutate({
        type: 'celular',
        celular: smsSend
          ? smsSend?.replace(/[^a-zA-Z0-9 ]/g, '').replaceAll(' ', '')
          : null,
        cpf: data.cpf
      });
    }
    setSmsError(undefined);
  };

  console.log({ data });

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent maxW={600}>
        <ModalHeader>Selecione uma forma de contato</ModalHeader>
        <ModalCloseButton />
        <Divider />

        <ModalBody p={5}>
          <VStack gap={3}>
            <Flex gap={2} w="100%">
              <CustomFormControl
                type="email"
                ref={emailRef}
                label="Via e-mail"
                defaultValue={data?.email}
                isInvalid={!!emailError}
                errorMessage={emailError}
              />
              <Button
                rightIcon={<CarretRightIcon />}
                colorScheme="orange"
                px={10}
                py={5}
                //variant="outline"
                onClick={handleEmailSubmit}
                isLoading={emailMutation.isLoading}
                loadingText={'Enviando...'}
              >
                Solicitar
              </Button>
            </Flex>
            <Flex gap={2} w="100%">
              <CustomFormControl
                type="tel"
                mask="(99) 9 9999-9999"
                ref={smsRef}
                label="Via SMS"
                defaultValue={formatNumber(data?.celular || '')}
                isInvalid={!!smsError}
                errorMessage={smsError}
              />
              <Button
                rightIcon={<CarretRightIcon />}
                colorScheme="orange"
                px={10}
                py={5}
                //variant="outline"
                onClick={handleSMSSubmit}
                isLoading={smsMutation.isLoading}
                loadingText={'Enviando...'}
              >
                Solicitar
              </Button>
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type CustomFormControlProps = {
  label: string;
  defaultValue?: string;
  type?: React.HTMLInputTypeAttribute;
  isInvalid?: boolean;
  errorMessage?: string;
  mask?: string;
};

const CustomFormControl = React.forwardRef<
  HTMLInputElement,
  CustomFormControlProps
>((props, ref: any) => {
  const [inputDisabled, setInputDisabled] = useState(true);

  const handleInputEdit = () => {
    setInputDisabled(false);

    setTimeout(() => {
      if (props.mask) return ref?.current.getInputDOMNode().focus();

      if (ref != null && typeof ref !== 'function' && ref.current !== null) {
        ref.current?.focus();
        ref.current?.select();
      }
    }, 100);
  };

  console.log(props.defaultValue);
  return (
    <FormControl as="fieldset" isInvalid={props.isInvalid || false}>
      <Box w="100%">
        <InputGroup
          borderColor={'#929292'}
          _after={{
            content: `'${props.label}'`,
            fontSize: '0.7rem',
            position: 'absolute',
            top: -2,
            left: 13,
            bg: '#FFFFFF',
            px: 2,
            zIndex: 100,
            color: '#929292'
          }}
        >
          {props.mask ? (
            <Input
              as={InputMask}
              type={props.type}
              ref={ref}
              placeholder={props.defaultValue}
              disabled={inputDisabled}
              mask={props.mask}
            />
          ) : (
            <Input
              type={props.type}
              ref={ref}
              placeholder={props.defaultValue}
              disabled={inputDisabled}
            />
          )}

          <InputRightElement bg={'transparent'}>
            <IconButton
              _focus={{ outline: '0 !important' }}
              variant={'link'}
              aria-label="Editar"
              icon={<PencilIcon />}
              bg="transparent"
              onClick={handleInputEdit}
            />
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>{props.errorMessage}</FormErrorMessage>
      </Box>
    </FormControl>
  );
});

CustomFormControl.displayName = 'CustomFormControl';
